/**
 * Exotel WebSDK Service
 * Handles VOIP calling through Exotel's official WebSDK
 * This replaces the direct JsSIP/SIP approach with Exotel's proprietary SDK
 */

// @ts-ignore - Exotel SDK doesn't have TypeScript definitions
import ExotelCRMWebSDK from 'exotel-ip-calling-crm-websdk';

export interface CallSession {
  id: string;
  status: 'ringing' | 'active' | 'ended' | 'failed' | 'incoming';
  duration: number;
  remoteNumber: string;
}

export interface ExotelCallEvent {
  callAnswerTime?: string;
  callDirection?: string;
  callDuration?: string;
  callEndReason?: string;
  callEndedTime?: string;
  callEstablishedTime?: string;
  callFromNumber?: string;
  callId?: string;
  callSid?: string;
  callStartedTime?: string;
  callState?: string;
  remoteDisplayName?: string;
  remoteId?: string;
  sessionId?: string;
}

export interface DialResponse {
  CustomerId?: string;
  AppId?: string;
  CallSid?: string;
  ExotelAccountSid?: string;
  DialWhomNumber?: string;
  AppUserID?: string;
  VirtualNumber?: string;
  Direction?: string;
  CallStatus?: string;
  CallState?: string;
  ToNumber?: string;
  FromNumber?: string;
  TotalDuration?: number;
  CallRecordings?: string;
  AccountDomain?: string;
  TicketId?: string;
  CallDetail?: any;
  CreatedAt?: string;
  UpdatedAt?: string;
}

export interface IncomingCallData {
  callerNumber: string;
  candidateName?: string;
  candidateId?: string;
  callSid?: string;
}

export class ExotelWebSDKService {
  private webPhone: any = null;
  private crmWebSDK: any = null;
  private onCallStatusChange?: (status: CallSession) => void;
  private onIncomingCallCallback?: (data: IncomingCallData) => void;
  private callStartTime: number = 0;
  private currentCallSid: string = '';
  private isInitialized: boolean = false;
  private heartbeatInterval: NodeJS.Timer | null = null;
  private sessionId: string = crypto.randomUUID();
  private isOutboundCall: boolean = false; // Track if we initiated the call
  
  // Context for call end notification
  private currentCandidateId: string = '';
  private currentRequirementId: string = '';
  private currentRemoteNumber: string = '';

  constructor() {
    console.log('📞 ExotelWebSDKService: Initializing Exotel WebSDK Service');
  }
  
  /**
   * Set the current call context for proper backend notification on call end
   */
  setCallContext(candidateId: string, requirementId: string): void {
    this.currentCandidateId = candidateId;
    this.currentRequirementId = requirementId;
    console.log('[WebSDK] Call context set:', { candidateId, requirementId });
  }

  /**
   * Initialize the Exotel WebSDK with access token and user ID
   */
  async initialize(
    accessToken: string,
    userId: string,
    onStatusChange?: (status: CallSession) => void
  ): Promise<boolean> {
    try {
      console.log('📞 ExotelWebSDKService: Starting initialization', { userId });

      this.onCallStatusChange = onStatusChange;

      // Create Exotel CRM WebSDK instance
      // Third parameter (true) enables auto-connect for VOIP
      this.crmWebSDK = new ExotelCRMWebSDK(accessToken, userId, true);

      // Initialize the SDK
      this.webPhone = await this.crmWebSDK.Initialize(
        this.handleCallEvents.bind(this),
        this.handleRegistrationEvent.bind(this),
        this.handleSessionCallback.bind(this)
      );

      console.log('✅ ExotelWebSDKService: SDK initialized successfully');
      this.isInitialized = true;
      
      // Start heartbeat to maintain presence
      this.startHeartbeat();
      
      return true;
    } catch (error) {
      console.error('❌ ExotelWebSDKService: Initialization failed:', error);
      this.isInitialized = false;
      return false;
    }
  }

  /**
   * Start sending heartbeat to maintain presence in DynamoDB
   */
  private startHeartbeat(): void {
    // Send heartbeat every 30 seconds
    this.heartbeatInterval = setInterval(async () => {
      try {
        const apiClient = (await import('../lib/api/client')).apiClient;
        await apiClient.post('/api/v1/recruiter/voip-websdk-heartbeat', {
          session_id: this.sessionId,
          page_url: window.location.href
        });
        console.log('[WebSDK] ❤️ Heartbeat sent');
      } catch (error) {
        console.warn('[WebSDK] ⚠️ Heartbeat failed:', error);
      }
    }, 30000) as any; // Every 30 seconds
    
    console.log('[WebSDK] ❤️ Heartbeat started, session:', this.sessionId);
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval as any);
      this.heartbeatInterval = null;
      console.log('[WebSDK] ❤️ Heartbeat stopped');
    }
  }

  /**
   * Set incoming call handler
   */
  setIncomingCallHandler(callback: (data: IncomingCallData) => void): void {
    this.onIncomingCallCallback = callback;
    console.log('[WebSDK] 📞 Incoming call handler registered');
  }

  /**
   * Lookup candidate by phone number
   */
  private async lookupCandidate(phoneNumber: string): Promise<{
    found: boolean;
    candidate_id?: string;
    name?: string;
    email?: string;
  } | null> {
    try {
      const apiClient = (await import('../lib/api/client')).apiClient;
      const response = await apiClient.get(`/api/v1/recruiter/candidates/lookup?phone=${encodeURIComponent(phoneNumber)}`);
      return response;
    } catch (error) {
      console.warn('[WebSDK] Could not lookup candidate:', error);
      return null;
    }
  }

  /**
   * Handle call events from Exotel SDK
   */
  private handleCallEvents(eventType: string, ...args: any[]): void {
    console.log('📞 ExotelWebSDKService: Call event received:', eventType, args);

    const callData = args[0] as ExotelCallEvent;

    switch (eventType) {
      case 'incoming':
        console.log('📱 Incoming call event from:', callData.remoteId || 'Unknown');
        
        // Check if this is truly an incoming call or just the agent leg of our outbound call
        if (this.isOutboundCall) {
          console.log('✅ This is the agent leg of our outbound call - auto-accepting');
          // This is our outbound call's agent leg - auto-accept to bridge to candidate
          this.acceptCall();
          this.isOutboundCall = false; // Reset flag
        } else {
          console.log('📞 This is a true incoming call - showing modal');
          // This is a real incoming call - show modal for accept/reject
          this.handleIncomingCallEvent(callData);
        this.notifyStatusChange('incoming', callData.remoteId || 'Unknown');
        }
        break;

      case 'connected':
        console.log('✅ Call connected');
        this.callStartTime = Date.now();
        this.currentCallSid = callData.callSid || '';
        this.notifyStatusChange('active', callData.remoteId || this.currentCallSid);
        break;

      case 'callEnded':
        console.log('📞 Call ended');
        // Notify backend about call completion BEFORE cleanup
        this.notifyBackendCallEnded('completed', callData);
        this.notifyStatusChange('ended', callData.remoteId || this.currentCallSid);
        this.cleanup();
        break;

      case 'ringing':
        console.log('📞 Call is ringing');
        this.notifyStatusChange('ringing', callData.remoteId || '');
        break;

      case 'failed':
        console.error('❌ Call failed:', callData.callEndReason);
        // Notify backend about call failure BEFORE cleanup
        this.notifyBackendCallEnded('failed', callData);
        this.notifyStatusChange('failed', callData.remoteId || '');
        this.cleanup();
        break;

      case 'holdtoggle':
        console.log('🔇 Hold toggled');
        break;

      case 'mutetoggle':
        console.log('🔇 Mute toggled');
        break;

      default:
        console.log('📞 Unknown call event:', eventType);
        break;
    }
  }

  /**
   * Handle registration events
   */
  private handleRegistrationEvent(event: string): void {
    console.log('📞 ExotelWebSDKService: Registration event:', event);

    if (event === 'registered') {
      console.log('✅ Device registered with Exotel');
    } else if (event === 'unregistered') {
      console.log('⚠️ Device unregistered from Exotel');
    }
  }

  /**
   * Handle session callbacks (optional)
   */
  private handleSessionCallback(...args: any[]): void {
    console.log('📞 ExotelWebSDKService: Session callback:', args);
  }

  /**
   * Handle incoming call event
   */
  private async handleIncomingCallEvent(callData: ExotelCallEvent): Promise<void> {
    try {
      const callerNumber = callData.remoteId || callData.callFromNumber || 'Unknown';
      this.currentCallSid = callData.callSid || callData.callId || '';
      
      console.log('[WebSDK] 📞 Processing incoming call from:', callerNumber);
      
      // Lookup candidate info for caller ID
      const candidateInfo = await this.lookupCandidate(callerNumber);
      
      // Trigger incoming call callback (will show modal + notification)
      if (this.onIncomingCallCallback) {
        this.onIncomingCallCallback({
          callerNumber,
          candidateName: candidateInfo?.name,
          candidateId: candidateInfo?.candidate_id,
          callSid: this.currentCallSid
        });
      } else {
        console.warn('[WebSDK] ⚠️ No incoming call handler registered, auto-accepting');
        // Fallback: auto-accept if no handler
        this.acceptCall();
      }
    } catch (error) {
      console.error('[WebSDK] ❌ Error handling incoming call:', error);
      // Fallback: auto-accept on error
      this.acceptCall();
    }
  }

  /**
   * Make an outbound call
   */
  async makeCall(phoneNumber: string, customField?: string): Promise<{ success: boolean; callSid?: string; message: string }> {
    try {
      // Only block if we truly don't have any SDK instance we can use to dial
      if (!this.webPhone && !this.crmWebSDK) {
        return {
          success: false,
          message: 'VOIP not initialized. Please try again.'
        };
      }

      // Validate phone number format (10-14 digits, optional +)
      if (!/^\+?[0-9]{10,14}$/.test(phoneNumber)) {
        return {
          success: false,
          message: 'Invalid phone number format'
        };
      }

      console.log('📞 ExotelWebSDKService: Making call to:', phoneNumber);
      
      // Mark as outbound call so we don't show incoming call modal
      this.isOutboundCall = true;
      
      // Log call initiation immediately to backend
      try {
        const apiClient = (await import('../lib/api/client')).apiClient;
        const callLogData = customField ? JSON.parse(customField) : {};
        
        await apiClient.post('/api/v1/recruiter/voip-call-log', {
          candidate_id: callLogData.candidate_id,
          requirement_id: callLogData.requirement_id,
          session_id: callLogData.session_id || this.sessionId,
          candidate_phone: phoneNumber,
          voip_session_id: this.sessionId
        });
        
        console.log('[WebSDK] ✅ Call logged to backend');
      } catch (logError) {
        console.warn('[WebSDK] ⚠️ Failed to log call to backend:', logError);
        // Don't block the call if logging fails
      }

      const dialer: any = this.webPhone || this.crmWebSDK;

      return new Promise((resolve) => {
        // Dial callback receives status and response
        const dialCallback = (status: string, response: { Data: DialResponse }) => {
          console.log('📞 ExotelWebSDKService: Dial callback:', { status, response });

          if (status === 'success') {
            const callSid = response?.Data?.CallSid || '';
            this.currentCallSid = callSid;

            console.log('✅ Call initiated successfully, CallSid:', callSid);

            resolve({
              success: true,
              callSid,
              message: 'Call initiated successfully'
            });
          } else {
            console.error('❌ Call initiation failed:', response);
            resolve({
              success: false,
              message: 'Failed to initiate call'
            });
          }
        };

        // Make the call using Exotel SDK
        dialer.MakeCall(phoneNumber, dialCallback, customField);
      });
    } catch (error) {
      console.error('❌ ExotelWebSDKService: Make call error:', error);
      return {
        success: false,
        message: `Failed to make call: ${error}`
      };
    }
  }

  /**
   * Accept incoming call (called from UI)
   */
  acceptCall(): void {
    console.log('[WebSDK] ✅ Accepting incoming call');
    try {
      if (this.webPhone) {
        console.log('📞 Accepting incoming call');
        this.webPhone.AcceptCall();
      }
    } catch (error) {
      console.error('❌ Failed to accept call:', error);
    }
  }

  /**
   * Reject an incoming call
   */
  rejectCall(): void {
    console.log('[WebSDK] ❌ Rejecting incoming call');
    try {
      if (this.webPhone) {
        this.webPhone.RejectCall();
        this.cleanup();
      }
    } catch (error) {
      console.error('[WebSDK] ❌ Failed to reject call:', error);
    }
  }

  /**
   * End/hang up the current call
   */
  hangup(): void {
    try {
      if (this.webPhone) {
        console.log('📞 Hanging up call');
        this.webPhone.HangupCall();
        this.cleanup();
      }
    } catch (error) {
      console.error('❌ Failed to hangup call:', error);
    }
  }

  /**
   * Alias for hangup
   */
  endCall(): void {
    this.hangup();
  }

  /**
   * Toggle mute state
   */
  toggleMute(): void {
    try {
      if (this.webPhone) {
        console.log('🔇 Toggling mute');
        this.webPhone.ToggleMute();
      }
    } catch (error) {
      console.error('❌ Failed to toggle mute:', error);
    }
  }

  /**
   * Toggle hold state
   */
  toggleHold(): void {
    try {
      if (this.webPhone) {
        console.log('⏸️ Toggling hold');
        this.webPhone.ToggleHold();
      }
    } catch (error) {
      console.error('❌ Failed to toggle hold:', error);
    }
  }

  /**
   * Send DTMF tone
   */
  sendDTMF(digit: string): void {
    try {
      if (this.webPhone) {
        console.log('📞 Sending DTMF:', digit);
        this.webPhone.SendDTMF(digit);
      }
    } catch (error) {
      console.error('❌ Failed to send DTMF:', error);
    }
  }

  /**
   * Register device manually (if auto-connect was disabled)
   */
  registerDevice(): void {
    try {
      if (this.webPhone) {
        console.log('📞 Registering device');
        this.webPhone.RegisterDevice();
      }
    } catch (error) {
      console.error('❌ Failed to register device:', error);
    }
  }

  /**
   * Unregister device
   */
  unregisterDevice(): void {
    try {
      if (this.webPhone) {
        console.log('📞 Unregistering device');
        this.webPhone.UnRegisterDevice();
        this.isInitialized = false;
      }
    } catch (error) {
      console.error('❌ Failed to unregister device:', error);
    }
  }

  /**
   * Get call duration in seconds
   */
  getCallDuration(): number {
    if (this.callStartTime === 0) return 0;
    return Math.floor((Date.now() - this.callStartTime) / 1000);
  }

  /**
   * Check if SDK is initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.webPhone !== null;
  }

  /**
   * Notify status change callback
   */
  private notifyStatusChange(
    status: 'ringing' | 'active' | 'ended' | 'failed' | 'incoming',
    remoteNumber: string
  ): void {
    if (this.onCallStatusChange) {
      this.onCallStatusChange({
        id: this.currentCallSid || Date.now().toString(),
        status,
        duration: this.getCallDuration(),
        remoteNumber
      });
    }
  }

  /**
   * Get the correct API base URL based on the current hostname
   */
  private getApiBaseUrl(): string {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isProdByHostname = hostname === 'macha.otomeyt.ai' || hostname === 'recruitedge.otomeyt.ai';
    
    // DEV API: emjbgkwgr8, PROD API: 6oi3xs2kzb
    const DEV_API = 'https://emjbgkwgr8.execute-api.ap-southeast-1.amazonaws.com';
    const PROD_API = 'https://6oi3xs2kzb.execute-api.ap-southeast-1.amazonaws.com';
    
    return isProdByHostname ? PROD_API : DEV_API;
  }

  /**
   * Notify backend when call ends (completed or failed)
   * This ensures the call status is properly updated in the database
   */
  private async notifyBackendCallEnded(status: 'completed' | 'failed' | 'no-answer' | 'busy', callData?: ExotelCallEvent): Promise<void> {
    try {
      const callSid = this.currentCallSid || callData?.callSid || callData?.callId || `voip_session_${this.sessionId}`;
      const duration = this.getCallDuration();
      
      console.log(`[WebSDK] 📤 Notifying backend of call end: ${callSid}, status=${status}, duration=${duration}s`);
      
      // Get access token from localStorage
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        console.warn('[WebSDK] No access token available for backend notification');
        return;
      }
      
      // Build payload
      const payload = {
        call_sid: callSid,
        status: status,
        duration: duration,
        candidate_id: this.currentCandidateId || undefined,
        requirement_id: this.currentRequirementId || undefined,
        remote_number: this.currentRemoteNumber || callData?.remoteId || undefined
      };
      
      // Get the correct API base URL for the current environment
      const apiBaseUrl = this.getApiBaseUrl();
      const apiUrl = `${apiBaseUrl}/api/v1/recruiter/voip-call-end`;
      
      console.log(`[WebSDK] 📤 Sending to: ${apiUrl}`);
      
      // Send to backend - fire and forget (don't block call cleanup)
      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      })
      .then(response => {
        if (response.ok) {
          console.log('[WebSDK] ✅ Backend notified of call end');
        } else {
          console.warn('[WebSDK] ⚠️ Backend notification failed:', response.status);
        }
      })
      .catch(error => {
        console.error('[WebSDK] ❌ Failed to notify backend:', error);
      });
      
    } catch (error) {
      console.error('[WebSDK] ❌ Error in notifyBackendCallEnded:', error);
    }
  }

  /**
   * Cleanup call resources
   */
  private cleanup(): void {
    this.currentCallSid = '';
    this.callStartTime = 0;
    this.isOutboundCall = false; // Reset outbound flag
    this.currentCandidateId = '';
    this.currentRequirementId = '';
    this.currentRemoteNumber = '';
  }

  /**
   * Destroy the service
   */
  destroy(): void {
    try {
      // Stop heartbeat
      this.stopHeartbeat();
      
      this.unregisterDevice();
      this.webPhone = null;
      this.crmWebSDK = null;
      this.isInitialized = false;
      console.log('📞 ExotelWebSDKService destroyed');
    } catch (error) {
      console.error('❌ Failed to destroy service:', error);
    }
  }
}

